/**
 * "Iniciar nova conversa" (13.21b, FR-91; itens I e N do chamado de 26/10/2026; protótipo v2, o ✎
 * ao lado da busca do WhatsApp).
 *
 * **O botão** é o lápis do protótipo, com nome acessível: ele só aparece para quem tem
 * `enviar_mensagem` ("Iniciar conversa segue 'Enviar mensagem'", FR-61-A) — esconder é conveniência,
 * quem recusa é o serviço (`403`). Fora de `conectado` ele fica travado e aponta, por
 * `aria-describedby`, a faixa de sempre da situação do número.
 *
 * **O diálogo** tem o campo "Telefone" com a máscara do FR-26 e os botões "Cancelar" e "Iniciar
 * conversa". O serviço confere no WhatsApp antes de abrir (`/chat/check`), e **a recusa aparece
 * verbatim abaixo do campo** — "Este numero nao tem WhatsApp." inclusive, sem acento, como o
 * servidor a escreve (convenção de 24/09/2026: o SPA nunca reescreve a mensagem do backend). O
 * diálogo fica aberto na recusa, e fecha no sucesso, com a conversa aberta pela tela.
 *
 * **Em portal, sobre `document.body`**, com o véu dos modais do SPA: no computador uma caixa
 * centrada, e no celular a tela cheia (a virada é de árvore, por `useEhCelular`, como nos outros
 * modais). `Esc` e o clique no véu fecham — menos enquanto o pedido está no ar, quando nada fecha.
 */

import { type FormEvent, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { aoDigitarTelefone, telefoneParaGravar } from '@/telefone/mascara-do-telefone'
import { type Conversa, useIniciarConversa } from './api'
import './iniciar-nova-conversa.css'

/** O nome acessível (e o `title`) do lápis, e o título do diálogo. */
export const INICIAR_NOVA_CONVERSA = 'Iniciar nova conversa'
export const ROTULO_DO_TELEFONE = 'Telefone'
export const APOIO_DO_TELEFONE =
  'O número da pessoa, com DDD. O CRM confere no WhatsApp antes de abrir a conversa.'
export const CANCELAR = 'Cancelar'
export const INICIAR_CONVERSA = 'Iniciar conversa'

function mensagemDe(erro: unknown): string {
  return erro instanceof ErroDaApi ? erro.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/** O lápis ao lado da busca. `travadoPor` é o `id` da faixa que explica a trava. */
export function BotaoDeIniciarConversa({
  aoAbrir,
  travadoPor,
}: {
  aoAbrir: () => void
  travadoPor: string | undefined
}) {
  return (
    <button
      type="button"
      className="conversa-nova__lapis"
      aria-label={INICIAR_NOVA_CONVERSA}
      title={INICIAR_NOVA_CONVERSA}
      disabled={travadoPor !== undefined}
      aria-describedby={travadoPor}
      onClick={aoAbrir}
      data-iniciar-nova-conversa=""
    >
      <span aria-hidden="true">✎</span>
    </button>
  )
}

export interface PropsDoDialogo {
  ehCelular: boolean
  aoFechar: () => void
  /** A conversa que o serviço abriu (ou achou) — a tela a abre. */
  aoIniciar: (conversa: Conversa) => void
}

/** O diálogo do "Iniciar nova conversa", em portal. */
export function DialogoDeIniciarConversa({ ehCelular, aoFechar, aoIniciar }: PropsDoDialogo) {
  const iniciar = useIniciarConversa()
  const [telefone, setTelefone] = useState('')
  const campo = useRef<HTMLInputElement>(null)
  const caixa = useRef<HTMLDivElement>(null)
  const idDoTitulo = useId()
  const idDoApoio = useId()
  const idDaRecusa = useId()
  const ocupado = iniciar.isPending
  const formato = ehCelular ? 'celular' : 'computador'

  useEffect(() => {
    campo.current?.focus()
  }, [])

  const fechar = () => {
    if (!ocupado) {
      aoFechar()
    }
  }

  useEffect(() => {
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape' && !evento.defaultPrevented) {
        /* Só segura o `Esc` quando de fato fecha (revisão): com o pedido no ar, ele passa adiante. */
        if (!iniciar.isPending) {
          evento.preventDefault()
          aoFechar()
        }
        return
      }
      /* O foco fica no diálogo: Tab no último volta ao primeiro, e Shift+Tab no primeiro vai ao
         último — o resto da página está atrás do véu. */
      if (evento.key !== 'Tab' || caixa.current === null) {
        return
      }
      const focaveis = [
        ...caixa.current.querySelectorAll<HTMLElement>('input, button:not(:disabled)'),
      ]
      const primeiro = focaveis[0]
      const ultimo = focaveis[focaveis.length - 1]
      if (primeiro === undefined || ultimo === undefined) {
        return
      }
      if (evento.shiftKey && document.activeElement === primeiro) {
        evento.preventDefault()
        ultimo.focus()
      } else if (!evento.shiftKey && document.activeElement === ultimo) {
        evento.preventDefault()
        primeiro.focus()
      }
    }
    document.addEventListener('keydown', aoTeclar, true)
    return () => {
      document.removeEventListener('keydown', aoTeclar, true)
    }
  }, [aoFechar, iniciar.isPending])

  const enviar = (evento: FormEvent) => {
    evento.preventDefault()
    if (ocupado) {
      return
    }
    iniciar.mutate(telefoneParaGravar(telefone), { onSuccess: aoIniciar })
  }

  const recusa = iniciar.isError ? mensagemDe(iniciar.error) : null

  return createPortal(
    <div className="conversa-nova__veu" data-formato={formato} role="presentation" onClick={fechar}>
      <div
        ref={caixa}
        className="conversa-nova__dialogo"
        data-formato={formato}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idDoTitulo}
        onClick={(evento) => {
          evento.stopPropagation()
        }}
      >
        <form className="conversa-nova__formulario" noValidate onSubmit={enviar}>
          <h2 className="conversa-nova__titulo" id={idDoTitulo}>
            {INICIAR_NOVA_CONVERSA}
          </h2>
          <div className="conversa-nova__corpo">
            <label className="conversa-nova__rotulo" htmlFor={`${idDoTitulo}-telefone`}>
              {ROTULO_DO_TELEFONE}
            </label>
            <input
              ref={campo}
              id={`${idDoTitulo}-telefone`}
              className="conversa-nova__campo"
              type="tel"
              inputMode="tel"
              autoComplete="off"
              placeholder="(00) 00000-0000"
              value={telefone}
              aria-describedby={recusa === null ? idDoApoio : `${idDaRecusa} ${idDoApoio}`}
              aria-invalid={recusa === null ? undefined : true}
              onChange={(evento) => {
                setTelefone(aoDigitarTelefone(evento, telefone))
                if (iniciar.isError) {
                  iniciar.reset()
                }
              }}
            />
            {recusa === null ? null : (
              <p className="conversa-nova__recusa" id={idDaRecusa} role="alert">
                {recusa}
              </p>
            )}
            <p className="conversa-nova__apoio" id={idDoApoio}>
              {APOIO_DO_TELEFONE}
            </p>
          </div>
          <div className="conversa-nova__rodape">
            <button
              type="button"
              className="conversa-nova__botao"
              disabled={ocupado}
              onClick={fechar}
            >
              {CANCELAR}
            </button>
            <button
              type="submit"
              className="conversa-nova__botao conversa-nova__botao--primario"
              disabled={ocupado || telefone.trim() === ''}
              aria-busy={ocupado || undefined}
            >
              {INICIAR_CONVERSA}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  )
}
